/**
 * Allowlist HTML sanitizer for server-authored rich text (CKEditor content).
 *
 * Career-lesson `content` is HTML written by staff in the ERP and injected with
 * `dangerouslySetInnerHTML`. Even though the author is trusted-ish, an
 * allowlist is the only thing that makes that injection safe: it neutralizes a
 * compromised staff account, a stored-XSS payload pasted into the editor, and
 * any future field that reuses this renderer.
 *
 * The repo has no sanitizer dependency, so this is a small DOM-based pass:
 * parse in an inert document (DOMParser — scripts never execute there), drop
 * every element not on ALLOWED_TAGS, drop every attribute not on the per-tag
 * allowlist, and reject any URL whose scheme is not http/https/mailto (this is
 * what blocks `javascript:` hrefs).
 *
 * Deliberately NOT allowed: script, style, iframe, object, embed, form, input,
 * link, meta, svg — and every `on*` handler attribute, which the attribute
 * allowlist excludes by construction.
 */

const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'figure',
  'figcaption',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

/** Attributes kept per tag. Anything absent here (incl. every `on*`) is dropped. */
const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'title', 'width', 'height'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan'],
};

/** Attributes whose value is a URL and therefore needs a scheme check. */
const URL_ATTRIBUTES = new Set(['href', 'src']);

const SAFE_URL_SCHEMES = ['http:', 'https:', 'mailto:'];

/**
 * True for values safe to put in href/src. Relative URLs are allowed (they
 * resolve against our own origin); absolute ones must use a safe scheme.
 * Anything unparsable is rejected rather than passed through.
 */
const isSafeUrl = (value: string): boolean => {
  const trimmed = value.trim();

  if (!trimmed) {
    return false;
  }

  // Control characters are how `java\nscript:` style bypasses are smuggled in.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) {
    return false;
  }

  try {
    // A relative URL resolves against the base and inherits our own scheme.
    const url = new URL(trimmed, window.location.origin);
    return SAFE_URL_SCHEMES.includes(url.protocol);
  } catch {
    return false;
  }
};

/**
 * Strips a single element down to allowed attributes, or unwraps/removes it
 * when the tag itself is not allowed.
 *
 * Disallowed *formatting* wrappers are unwrapped (children kept) so that a
 * `<font>` or a stray `<section>` does not silently swallow a paragraph of
 * text; disallowed *executable/embedding* tags are removed with their subtree,
 * since their content is code, not prose.
 */
const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'link',
  'meta',
  'base',
  'svg',
  'math',
  'template',
  'noscript',
]);

const cleanElement = (element: Element): void => {
  const tag = element.tagName.toLowerCase();

  if (DROP_WITH_CONTENT.has(tag)) {
    element.remove();
    return;
  }

  // Recurse first: unwrapping a parent must not skip its children.
  Array.from(element.children).forEach(cleanElement);

  if (!ALLOWED_TAGS.has(tag)) {
    element.replaceWith(...Array.from(element.childNodes));
    return;
  }

  const allowed = ALLOWED_ATTRIBUTES[tag] ?? [];

  Array.from(element.attributes).forEach(({ name, value }) => {
    const attribute = name.toLowerCase();

    if (!allowed.includes(attribute)) {
      element.removeAttribute(name);
      return;
    }

    if (URL_ATTRIBUTES.has(attribute) && !isSafeUrl(value)) {
      element.removeAttribute(name);
    }
  });

  // External links open in a new tab without handing over window.opener.
  if (tag === 'a' && element.getAttribute('href')) {
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer');
  }
};

/**
 * Returns the sanitized HTML, safe to hand to `dangerouslySetInnerHTML`.
 * Empty/absent input yields an empty string.
 */
export const sanitizeRichText = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  // DOMParser builds an INERT document: <script> is not executed and
  // <img onerror> never fires while we walk the tree.
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  Array.from(parsed.body.children).forEach(cleanElement);

  return parsed.body.innerHTML;
};
